/**
 * TacticalMapChrome.tsx — Handler e overlay interni al MapContainer
 *
 * © 2024 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import * as L from 'leaflet';
import { useMap, useMapEvents } from 'react-leaflet';
import { getSymbolPath } from '@/config/sitac';
import type { SITACSymbolWithCategory } from './SITACSymbolSelector';
import { getWindLabel } from '../../map/layers/WindOverlay';
import { useMapStore } from '../../store/mapStore';

interface MapClickHandlerProps {
  placementMode: { active: boolean; symbol: SITACSymbolWithCategory | null };
  onPlaceSymbol: (lat: number, lng: number) => void;
  perimetroMode: boolean;
  onAddPuntoPerimetro: (lat: number, lng: number) => void;
  innescoMode?: boolean;
  onSetInnesco?: (lat: number, lng: number) => void;
  vedettaMode?: boolean;
  onSetVedetta?: (lat: number, lng: number) => void;
  mezzoTerrestreMode?: boolean;
  onSetMezzoTerrestre?: (lat: number, lng: number) => void;
}

interface MapViewUpdaterProps {
  center: [number, number];
  zoom: number;
  layoutVersion: number;
}

export const MapClickHandler: React.FC<MapClickHandlerProps> = ({
  placementMode,
  onPlaceSymbol,
  perimetroMode,
  onAddPuntoPerimetro,
  innescoMode = false,
  onSetInnesco,
  vedettaMode = false,
  onSetVedetta,
  mezzoTerrestreMode = false,
  onSetMezzoTerrestre,
}) => {
  useMapEvents({
    click(e) {
      // Priorità: innesco > vedetta > mezzo terrestre > perimetro > simbolo
      if (innescoMode && onSetInnesco) {
        onSetInnesco(e.latlng.lat, e.latlng.lng);
        return;
      }
      if (vedettaMode && onSetVedetta) {
        onSetVedetta(e.latlng.lat, e.latlng.lng);
        return;
      }
      if (mezzoTerrestreMode && onSetMezzoTerrestre) {
        onSetMezzoTerrestre(e.latlng.lat, e.latlng.lng);
        return;
      }
      if (perimetroMode) {
        onAddPuntoPerimetro(e.latlng.lat, e.latlng.lng);
        return;
      }
      if (placementMode.active) {
        onPlaceSymbol(e.latlng.lat, e.latlng.lng);
      }
    },
  });
  return null;
};

export const MapStateSync: React.FC = () => {
  const map       = useMap();
  const setCenter = useMapStore((s) => s.setCenter);
  const setZoom   = useMapStore((s) => s.setZoom);

  useEffect(() => {
    const updateState = () => {
      const center = map.getCenter();
      setCenter([center.lat, center.lng]);
      setZoom(map.getZoom());
    };
    updateState();
    map.on('moveend', updateState);
    map.on('zoomend', updateState);
    return () => {
      map.off('moveend', updateState);
      map.off('zoomend', updateState);
    };
  }, [map, setCenter, setZoom]);

  return null;
};

export const MapViewUpdater: React.FC<MapViewUpdaterProps> = ({ center, zoom, layoutVersion }) => {
  const map = useMap();
  const lastAppliedRef = useRef<string>('');

  useEffect(() => {
    const signature = `${center[0].toFixed(6)}:${center[1].toFixed(6)}:${zoom}`;
    if (lastAppliedRef.current !== signature) {
      map.setView(center, zoom, { animate: false });
      lastAppliedRef.current = signature;
    }
  }, [map, center, zoom]);

  useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(false), 80);
    return () => window.clearTimeout(timer);
  }, [map, layoutVersion]);

  return null;
};

export const CenterCrosshairOverlay: React.FC<{ visible: boolean; color?: string }> = ({
  visible, color = '#ff4500',
}) => {
  if (!visible) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1150 }} aria-hidden="true">
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: 42, height: 42,
        transform: 'translate(-50%, -50%)', border: `2px solid ${color}`, borderRadius: '50%',
        boxShadow: `0 0 10px ${color}55`, background: 'transparent' }} />
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: 2, height: 68,
        transform: 'translate(-50%, -50%)', background: color, boxShadow: `0 0 8px ${color}88` }} />
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: 68, height: 2,
        transform: 'translate(-50%, -50%)', background: color, boxShadow: `0 0 8px ${color}88` }} />
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: 8, height: 8,
        transform: 'translate(-50%, -50%)', borderRadius: '50%', background: color,
        boxShadow: `0 0 10px ${color}` }} />
    </div>
  );
};

export const CenterSymbolPreview: React.FC<{
  visible: boolean;
  symbol: SITACSymbolWithCategory | null;
}> = ({ visible, symbol }) => {
  if (!visible || !symbol) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1140 }}>
      <div style={{ position: 'absolute', left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)', width: 56, height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: 0.75, filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.35))' }}>
        <img src={getSymbolPath(symbol.categoria, symbol.file)}
          alt={symbol.descrizione}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
    </div>
  );
};

export const GridOverlay: React.FC<{ visible: boolean }> = ({ visible }) => {
  if (!visible) return null;
  return (
    <div style={{
      position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 900,
      backgroundImage:
        'linear-gradient(rgba(0,0,0,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.07) 1px, transparent 1px)',
      backgroundSize: '50px 50px',
    }} aria-hidden="true" />
  );
};

export const MapInstanceBridge: React.FC<{ onMap: (map: L.Map) => void }> = ({ onMap }) => {
  const map = useMap();
  const cb  = useCallback(() => { onMap(map); }, [map, onMap]);
  useEffect(() => { cb(); }, [cb]);
  return null;
};

export const WindVectorOverlay: React.FC<{
  directionDeg: number;
  speedKmh: number;
  visible: boolean;
}> = ({ directionDeg, speedKmh, visible }) => {
  if (!visible) return null;

  const propagationDeg   = ((directionDeg + 180) % 360 + 360) % 360;
  const provenanceLabel  = getWindLabel(directionDeg);
  const propagationLabel = getWindLabel(propagationDeg);

  return (
    <div style={{ position: 'absolute', left: 10, top: 10, zIndex: 1200, pointerEvents: 'none', display: 'grid', gap: 8 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'rgba(15, 23, 42, 0.92)', color: '#f8fafc',
        borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
        padding: '8px 10px', boxShadow: '0 8px 20px rgba(0,0,0,0.28)',
      }}>
        <div style={{
          fontSize: '2rem', lineHeight: 1,
          transform: `rotate(${directionDeg}deg)`, transformOrigin: 'center',
          filter: 'drop-shadow(0 0 8px rgba(125,211,252,0.35))',
        }}>↑</div>
        <div style={{ display: 'grid', gap: 2 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 800 }}>Vento da {provenanceLabel}</div>
          <div style={{ fontSize: '0.75rem', color: '#cbd5e1' }}>
            Spinta fronte verso {propagationLabel} · {Math.round(speedKmh)} km/h
          </div>
        </div>
      </div>
    </div>
  );
};
